import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { messaging } from "../services/messaging.js";
import {
  enableNotifications,
  hasPushSubscription,
  notificationPermission,
  prepareNotifications,
  notificationSupport,
} from "../notifications.js";

const DISMISS_KEY = "tw-notifications-prompt-dismissed";

function readDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * Home-screen banner that gets notifications switched on — or, on an iPhone
 * in a Safari tab, explains that they need the Home Screen app first.
 */
export function NotificationsPrompt() {
  const support = notificationSupport();
  const [permission, setPermission] = useState(notificationPermission);
  const [dismissed, setDismissed] = useState(readDismissed);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Allowed but never actually registered — nothing would ever arrive.
  const [unregistered, setUnregistered] = useState(false);
  useEffect(() => {
    if (permission === "granted") void hasPushSubscription().then((has) => setUnregistered(!has));
  }, [permission]);
  useEffect(() => {
    if (support === "supported") void prepareNotifications().catch(() => undefined);
  }, [support]);

  if (permission === "granted" && unregistered) {
    return (
      <div className="banner notify-prompt">
        <div className="notify-prompt-text">
          <strong>Notifications aren&apos;t set up on this device</strong>
          <span>They&apos;re allowed, but this phone isn&apos;t registered, so no banners will arrive.</span>
        </div>
        <div className="notify-prompt-actions">
          <Link to="/settings"><button className="primary">Fix</button></Link>
        </div>
      </div>
    );
  }

  if (dismissed || support === "unsupported" || permission === "granted") return null;

  function dismiss() {
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* dismissal just won't be remembered */
    }
    setDismissed(true);
  }

  async function turnOn() {
    const account = messaging.account;
    if (!account) return;
    setBusy(true);
    setError(null);
    try {
      setPermission(await enableNotifications(account));
    } catch (err) {
      console.error("enabling notifications failed", err);
      setError("Couldn't turn on notifications. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="banner notify-prompt">
      <div className="notify-prompt-text">
        {support === "ios-needs-install" ? (
          <>
            <strong>Get notified of messages and calls</strong>
            <span>
              On iPhone, notifications only work in the app on your Home Screen: tap Share (the square with
              an arrow), choose <em>Add to Home Screen</em>, then open TrueWave from there.
            </span>
          </>
        ) : permission === "denied" ? (
          <>
            <strong>Notifications are blocked</strong>
            <span>Allow them for TrueWave in your device or browser settings to hear about messages and calls while the app is closed.</span>
          </>
        ) : (
          <>
            <strong>Turn on notifications</strong>
            <span>Know about new messages and incoming calls while TrueWave is closed. They never show who it's from or what they said.</span>
          </>
        )}
        {error && <span className="error">{error}</span>}
      </div>
      <div className="notify-prompt-actions">
        {support === "supported" && permission === "default" && (
          <button className="primary" onClick={() => void turnOn()} disabled={busy}>
            {busy ? "…" : "Turn on"}
          </button>
        )}
        <button className="ghost notify-prompt-close" onClick={dismiss} aria-label="Dismiss">
          ✕
        </button>
      </div>
    </div>
  );
}
